import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";
import { isAdmin } from "utils/common";

import type { DealerLocation } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type IntegrationsProps = {
  tabId: string;
  location: DealerLocation;
};

export const Integrations = ({ tabId, location }: IntegrationsProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { watch, formState } = FormHelper.useFormContext();
  const [isShareboxEnabled, isEvhcExportEnabled, isRobnetEnabled] = watch(["is_sharebox_enabled", "evhc_export_enabled", "is_robnet_enabled"]);

  if (!user || !isAdmin(user.role_id)) return null;

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="plug" />
        </div>
        <span className="section-title-text">{t("v8_integrations", "Integrations")}</span>
      </div>

      <div className="dealer-location-subheading">{t("v8_sharebox_configuration", "Sharebox configuration")}</div>
      <Form.Group>
        <Form.Field width={16}>
          <FormHelper.Field.Checkbox name="is_sharebox_enabled" label={t("v8_enabled", "Enabled")} />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8} required={isShareboxEnabled} error={formState.isSubmitted && isShareboxEnabled && !watch("sharebox_api_client_id")}>
          <label>{t("v8_sharebox_api_client_id", "Sharebox api client ID")}</label>
          <FormHelper.Field.Input name="sharebox_api_client_id" />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_sharebox_api_client_secret", "Sharebox api client secret")}</label>
          <FormHelper.Field.Input
            name="sharebox_api_client_secret"
            preventAutofill
            inputProps={{ type: "password", placeholder: location.has_sharebox_api_client_secret ? ASTERISK_PLACEHOLDER : "" }}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8} required={isShareboxEnabled} error={formState.isSubmitted && isShareboxEnabled && !watch("sharebox_customer_id")}>
          <label>{t("v8_sharebox_customer_id", "Sharebox customer ID")}</label>
          <FormHelper.Field.Input name="sharebox_customer_id" />
        </Form.Field>
        <Form.Field width={8} required={isShareboxEnabled} error={formState.isSubmitted && isShareboxEnabled && !watch("sharebox_session_id")}>
          <label>{t("v8_sharebox_session_id", "Sharebox session ID")}</label>
          <FormHelper.Field.Input name="sharebox_session_id" />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_sharebox_weblink_access_key", "Sharebox weblink access key")}</label>
          <FormHelper.Field.Input
            name="sharebox_weblink_access_key"
            preventAutofill
            inputProps={{ type: "password", placeholder: location.has_sharebox_weblink_access_key ? ASTERISK_PLACEHOLDER : "" }}
          />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_sharebox_private_key", "Sharebox private key")}</label>
          <FormHelper.Field.Input
            name="sharebox_private_key"
            preventAutofill
            inputProps={{ type: "password", disabled: true, placeholder: location.has_sharebox_private_key ? ASTERISK_PLACEHOLDER : "" }}
          />
        </Form.Field>
      </Form.Group>

      <Divider />
      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_evhc", "JLR - EVHC - Export")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="evhc_export_enabled" label={t("v8_send_daily_evhc_export", "Send daily EVHC export")} />
          </Form.Field>
          <Form.Field required={isEvhcExportEnabled} error={formState.isSubmitted && isEvhcExportEnabled && !watch("evhc_primary_code")}>
            <label>{t("v8_evhc_primary_code", "EVHC Primary Code")}</label>
            <FormHelper.Field.Input name="evhc_primary_code" />
          </Form.Field>
          <Form.Field required={isEvhcExportEnabled} error={formState.isSubmitted && isEvhcExportEnabled && !watch("evhc_secondary_code")}>
            <label>{t("v8_evhc_secondary_code", "EVHC Secondary Code")}</label>
            <FormHelper.Field.Input name="evhc_secondary_code" />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_pon_configuration", "PON configuration")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_pon_enabled" label={t("v8_pon_enabled", "PON Enabled")} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_pon_location_number", "PON Location Number")}</label>
            <FormHelper.Field.Input name="pon_location_number" />
          </Form.Field>
        </div>
      </div>

      <Divider />
      <div className="dealer-location-subheading">{t("v8_robnet_configuration", "Robnet configuration")}</div>
      <Form.Group>
        <Form.Field width={16}>
          <FormHelper.Field.Checkbox name="is_robnet_enabled" label={t("v8_robnet", "Robnet")} />
        </Form.Field>
      </Form.Group>
      {isRobnetEnabled && (
        <Form.Group>
          <Form.Field width={8}>
            <label>{t("v8_robnet_api_key", "Robnet Api Key")}</label>
            <FormHelper.Field.Input
              name="robnet_api_key"
              preventAutofill
              inputProps={{ type: "password", placeholder: location.has_robnet_api_key ? ASTERISK_PLACEHOLDER : "" }}
            />
          </Form.Field>
          <Form.Field width={8}>
            <label>{t("v8_robnet_location_number", "Robnet Location Number")}</label>
            <FormHelper.Field.Input nullable name="robnet_location_number" inputProps={{ type: "number", min: 0 }} />
          </Form.Field>
        </Form.Group>
      )}
    </Element>
  );
};
